import type { ReactNode } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2CardBody,
  V2Button,
  V2Badge,
  V2Tag,
  V2Avatar,
  V2Kpi,
  V2MetricStrip,
  V2Table,
  V2ProgressBar,
  V2EmptyState,
  V2CheckRow,
  V2SearchInput,
  V2BottomNav,
  LoadingState,
  StatePanel,
  Icon,
  type V2TableColumn,
  type V2BadgeTone,
  type V2ButtonVariant,
  type V2BottomNavItem,
} from '@sakhtar/design-system/react';
import { BarChart, DonutChart, LineChart } from '../../lib/charts/index.js';
import { MOBILE_NAV } from '../../shell/navConfig.js';
import { OperatorPage } from './OperatorShell.js';

const BUTTON_VARIANTS: readonly V2ButtonVariant[] = ['primary', 'secondary', 'ghost', 'danger'];
const BADGE_TONES: readonly V2BadgeTone[] = [
  'neutral',
  'success',
  'warn',
  'danger',
  'info',
  'brand',
];

const BRAND_RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

const SEMANTIC_SWATCHES: readonly { readonly name: string; readonly var: string }[] = [
  { name: 'success', var: '--sk-success' },
  { name: 'warn', var: '--sk-warn' },
  { name: 'danger', var: '--sk-danger' },
  { name: 'info', var: '--sk-info' },
];

const CHART_ORDER_VARS = [
  '--sk-chart-1',
  '--sk-chart-6',
  '--sk-chart-3',
  '--sk-chart-2',
  '--sk-chart-4',
  '--sk-chart-5',
] as const;

const DESIGN_REFERENCES: readonly string[] = [
  'Sakhtar Cloud ERP - Final.dc.html — shell IA (sidebar, topbar, mobile nav)',
  'Sakhtar Pilot P0 — auth family, security console, subscription, master data',
  'Sakhtar Sales CRM.dc.html — CRM hub, leads, opportunities, quotes, orders',
  'Sakhtar Finance.dc.html — accounts, journals, AR/AP, treasury, VAT',
  'Sakhtar Inventory.dc.html — stock, receipts/issues/transfers, adjustments, reservations',
  'Sakhtar Procurement.dc.html — purchase requests, purchase orders, suppliers',
  'Sakhtar Projects and Tasks.dc.html — projects hub, project list',
  'Sakhtar Mobile App.dc.html — mobile bottom nav, account/notification/menu sheets',
];

const MOBILE_COMPLIANCE_ROWS: readonly {
  readonly rule: string;
  readonly status: 'ok' | 'fixed-now';
  readonly note: string;
}[] = [
  {
    rule: 'دقیقاً پنج آیتم، بدون آیتم ششم',
    status: 'ok',
    note: 'MOBILE_NAV در navConfig.ts دقیقاً پنج مورد دارد: حساب کاربری، اعلان‌ها، افزودن، کارها، منو.',
  },
  {
    rule: 'ترتیب فیزیکی چپ‌به‌راست ثابت، حتی زیر dir=rtl',
    status: 'ok',
    note: '.sk-bottomnav با direction: ltr صریح override شده (components.css) — کامنت کد مستقیماً به این قاعده اشاره می‌کند.',
  },
  {
    rule: 'حالت فعال فقط با رنگ اعلام نمی‌شود',
    status: 'ok',
    note: '.sk-bottomnav__item.is-active همزمان نشانگر بالا (::before)، وزن ۶۰۰ و پس‌زمینهٔ ملایم برند را اعمال می‌کند.',
  },
  {
    rule: 'آیتم افزودن با دیسک برجسته و سایهٔ برند',
    status: 'ok',
    note: '.sk-bottomnav__item--primary .sk-bottomnav__fab یک دیسک ۵۲px با گرادیان برند رندر می‌کند.',
  },
  {
    rule: 'آیتم غیرفعال حذف نمی‌شود',
    status: 'ok',
    note: 'V2BottomNavItem.disabled فقط aria-disabled و کدری ۰٫۴۵ اعمال می‌کند؛ آیتم در DOM و چیدمان باقی می‌ماند.',
  },
  {
    rule: 'هر هدف لمسی حداقل ۴۴px',
    status: 'ok',
    note: '.sk-bottomnav__item با min-height: 52px — بیشتر از حداقل لازم.',
  },
  {
    rule: 'ناحیهٔ ایمن پایین صفحه رزرو می‌شود',
    status: 'ok',
    note: '.sk-bottomnav__safe-area (22px) + env(safe-area-inset-bottom) در padding نوار.',
  },
  {
    rule: 'حلقهٔ فوکوس روی هر عنصر قابل‌فوکوس دیده می‌شود',
    status: 'fixed-now',
    note: 'در ممیزی همین جلسه، .sk-bottomnav__item فاقد قاعدهٔ focus-visible بود — این حفرهٔ واقعی امروز در packages/design-system اصلاح شد.',
  },
];

function Section({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}) {
  return (
    <V2Card>
      <V2CardHeader title={title} subtitle={subtitle} />
      <V2CardBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
      </V2CardBody>
    </V2Card>
  );
}

function Swatch({ label, cssVar }: { readonly label: string; readonly cssVar: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 84 }}>
      <div
        style={{
          height: 44,
          borderRadius: 'var(--sk-r-md)',
          background: `var(${cssVar})`,
          border: '1px solid var(--sk-border)',
        }}
      />
      <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }} className="ltr">
        {label}
      </span>
    </div>
  );
}

function MobilePreviewFrame({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{label}</span>
      <div
        className="sk-uikit-mobile-preview"
        style={{
          position: 'relative',
          width: 320,
          height: 96,
          borderRadius: 'var(--sk-r-lg)',
          border: '1px solid var(--sk-border)',
          overflow: 'hidden',
          background: 'var(--sk-bg-canvas)',
        }}
      >
        {/* .sk-bottomnav is production `position: fixed` (pinned to the real
            viewport edge) — this scoped override contains it inside the
            preview frame instead, for side-by-side demonstration only. */}
        <style>{`.sk-uikit-mobile-preview .sk-bottomnav { position: absolute; }`}</style>
        {children}
      </div>
    </div>
  );
}

interface SampleRow {
  readonly id: string;
  readonly name: string;
  readonly amountRial: number;
  readonly statusLabel: string;
  readonly tone: V2BadgeTone;
}

const SAMPLE_ROWS: readonly SampleRow[] = [
  { id: '1', name: 'ردیف نمونه ۱', amountRial: 1_250_000, statusLabel: 'ثبت‌شده', tone: 'success' },
  { id: '2', name: 'ردیف نمونه ۲', amountRial: 480_000, statusLabel: 'در انتظار', tone: 'warn' },
  { id: '3', name: 'ردیف نمونه ۳', amountRial: 92_000, statusLabel: 'لغوشده', tone: 'neutral' },
];

/**
 * UI_REFERENCE_SOURCE:
 * section: no single Design Board — same living catalog as
 *   apps/client/src/routes/admin/UiKitPage.tsx (tenant-scoped), ported here
 *   for Sakhtar's own operator/super-admin, plus a dedicated mobile-shell
 *   compliance section against Sakhtar Mobile App.dc.html §FRAME 03 (BOTTOM
 *   NAVBAR — ALL STATES) and §FRAME 14 (MOBILE INTERACTION NOTES).
 * archetype: STATIC_REFERENCE — no tenant data, illustrative sample values
 *   only, clearly labeled as such.
 * state reference: n/a (static)
 *
 * This resolves the tenant UiKitPage's own documented "ACCESS GAP": that
 * page was built for operator/super-admin but apps/client had no real
 * operator-identity concept to gate it with. This page lives inside
 * OperatorShell, which already has one (getOperatorSession/operatorLogout,
 * see lib/operatorApi.js) — reachable only after real operator
 * authentication, at /internal/admin/ui-kit.
 *
 * The mobile bottom-nav preview renders the *actual* production
 * MOBILE_NAV config from shell/navConfig.ts (not a hand-copied duplicate),
 * so this page can never silently drift from what ships to tenants.
 */
export function UiKitPage() {
  const defaultItems: V2BottomNavItem[] = MOBILE_NAV.map((entry) => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    href: '#',
    active: entry.id === 'notifications',
    primary: entry.id === 'add',
    disabled: false,
  }));
  const disabledItems: V2BottomNavItem[] = MOBILE_NAV.map((entry) => ({
    id: entry.id,
    label: entry.label,
    icon: entry.icon,
    href: '#',
    active: false,
    primary: entry.id === 'add',
    disabled: entry.id === 'tasks' || entry.id === 'add',
  }));
  const noop = () => {};

  return (
    <OperatorPage title="کیت رابط کاربری">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div className="sk-frame-label">/internal/admin — راهنمای رابط کاربری</div>
          <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
            کاتالوگ زندهٔ اجزای طراحی v2 — رنگ‌ها، تایپوگرافی، کامپوننت‌ها، چارت‌ها و انطباق نوار
            پایین موبایل. این صفحه فقط مرجع است؛ داده‌های نمایش‌داده‌شده نمونه‌اند.
          </div>
        </div>

        <Section title="برند" subtitle="لوگو و نام پلتفرم — همان دارایی بالای ساید‌بار">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-brand-700)',
                display: 'grid',
                placeContent: 'center',
              }}
            >
              <Icon name="building" size="md" />
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>ساختار</div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>Sakhtar Cloud ERP</div>
            </div>
          </div>
        </Section>

        <Section
          title="رنگ‌ها"
          subtitle="طیف برند (--sk-brand-50..900)، رنگ‌های معنایی، و ترتیب ثابت چارت‌ها"
        >
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              طیف برند (Sakhtar Indigo)
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {BRAND_RAMP_STEPS.map((step) => (
                <Swatch key={step} label={`--sk-brand-${step}`} cssVar={`--sk-brand-${step}`} />
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              رنگ‌های معنایی (ثابت در هر دو تم)
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SEMANTIC_SWATCHES.map((s) => (
                <Swatch key={s.name} label={s.var} cssVar={s.var} />
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              ترتیب ثابت چارت‌ها — بر اساس موقعیت انتساب می‌شود، نه بازچینی
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CHART_ORDER_VARS.map((v, i) => (
                <Swatch key={v} label={`#${i + 1} ${v}`} cssVar={v} />
              ))}
            </div>
          </div>
        </Section>

        <Section title="تایپوگرافی" subtitle="مقیاس فونت v2 (--sk-fs-*)">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>2xl — عنوان صفحه</div>
            <div style={{ fontSize: 'var(--sk-fs-xl)', fontWeight: 700 }}>xl — عنوان بخش</div>
            <div style={{ fontSize: 'var(--sk-fs-lg)' }}>lg — متن برجسته</div>
            <div style={{ fontSize: 'var(--sk-fs-md)' }}>md — متن پایه</div>
            <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
              sm — متن فرعی
            </div>
            <div style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-subtle)' }}>
              xs — برچسب و متادیتا
            </div>
          </div>
        </Section>

        <Section title="دکمه‌ها" subtitle="V2Button — چهار حالت، سه اندازه">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {BUTTON_VARIANTS.map((variant) => (
              <V2Button key={variant} variant={variant}>
                {variant}
              </V2Button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <V2Button variant="primary" size="sm">
              sm
            </V2Button>
            <V2Button variant="primary" size="md">
              md
            </V2Button>
            <V2Button variant="primary" size="lg">
              lg
            </V2Button>
            <V2Button variant="primary" icon="plus">
              با آیکون
            </V2Button>
            <V2Button variant="secondary" disabled>
              غیرفعال
            </V2Button>
          </div>
        </Section>

        <Section title="نشان‌ها و برچسب‌ها" subtitle="V2Badge (شش تن) و V2Tag">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {BADGE_TONES.map((tone) => (
              <V2Badge key={tone} tone={tone}>
                {tone}
              </V2Badge>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <V2Tag>برچسب نمونه</V2Tag>
            <V2Avatar initials="ک" />
            <V2Avatar initials="کس" size="lg" />
          </div>
        </Section>

        <Section title="فرم و کنترل‌ها" subtitle="جست‌وجو، نوار پیشرفت، ردیف انتخاب">
          <V2SearchInput placeholder="جست‌وجوی نمونه" value="" onChange={noop} width={240} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
            <V2ProgressBar percent={35} />
            <V2ProgressBar percent={70} />
          </div>
          <V2CheckRow label="گزینهٔ نمونه" meta="متادیتا" checked onChange={noop} />
        </Section>

        <Section title="KPI و نوار متریک" subtitle="V2Kpi در V2MetricStrip">
          <V2MetricStrip>
            <V2Kpi label="نمونه شاخص ۱" value="۱۲۴" delta="+۸٪" deltaDirection="up" />
            <V2Kpi label="نمونه شاخص ۲" value="۳٬۴۵۰٬۰۰۰ ریال" />
            <V2Kpi label="نمونه شاخص ۳" value="۹" deltaDirection="down" delta="-۲" />
          </V2MetricStrip>
        </Section>

        <Section title="جدول" subtitle="V2Table با ردیف‌های نمونه">
          <V2Table
            columns={
              [
                { key: 'name', header: 'نام', render: (r) => r.name },
                {
                  key: 'amount',
                  header: 'مبلغ (ریال)',
                  align: 'end',
                  render: (r) => (
                    <span className="sk-num">{r.amountRial.toLocaleString('fa-IR')}</span>
                  ),
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (r) => <V2Badge tone={r.tone}>{r.statusLabel}</V2Badge>,
                },
              ] satisfies readonly V2TableColumn<SampleRow>[]
            }
            rows={SAMPLE_ROWS}
            zebra
          />
        </Section>

        <Section
          title="چارت‌ها (ChartKit)"
          subtitle="BarChart / DonutChart / LineChart — موتور ECharts، داده‌های نمونه"
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                BarChart
              </div>
              <BarChart
                ariaLabel="نمونهٔ نمودار میله‌ای"
                data={[
                  { label: 'فروردین', value: 12 },
                  { label: 'اردیبهشت', value: 18 },
                  { label: 'خرداد', value: 9 },
                  { label: 'تیر', value: 24 },
                ]}
                height={220}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                DonutChart
              </div>
              <DonutChart
                ariaLabel="نمونهٔ نمودار دونات"
                centerLabel="کل نمونه"
                data={[
                  { label: 'باز', value: 14 },
                  { label: 'در جریان', value: 8 },
                  { label: 'بسته', value: 22 },
                ]}
                height={220}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                LineChart
              </div>
              <LineChart
                ariaLabel="نمونهٔ نمودار خطی"
                categories={['هفته ۱', 'هفته ۲', 'هفته ۳', 'هفته ۴']}
                series={[
                  { name: 'سری الف', data: [5, 9, 7, 12] },
                  { name: 'سری ب', data: [3, 4, 8, 6] },
                ]}
                height={220}
              />
            </div>
          </div>
        </Section>

        <Section title="حالت‌های صفحه" subtitle="بارگذاری، خالی، خطا/دسترسی">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                LoadingState
              </div>
              <LoadingState />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                V2EmptyState
              </div>
              <V2EmptyState icon="info" title="موردی برای نمایش نیست" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
                StatePanel — network_error
              </div>
              <StatePanel variant="network_error" title="اطلاعات قابل نمایش نیست" />
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
            توجه: هیچ‌کدام «اسکلت بارگذاری» (loading skeleton، shimmer، shape-of-content) طبق
            Sakhtar Mobile App.dc.html §FRAME 12 نیست — LoadingState فعلی یک spinner است، نه
            skeleton. حفرهٔ واقعی و شناخته‌شده، هنوز رفع‌نشده (نیازمند یک کامپوننت جدید در
            design-system، نه فیکس این صفحه).
          </div>
        </Section>

        <Section
          title="انطباق نوار پایین موبایل"
          subtitle="در برابر Sakhtar Mobile App.dc.html §FRAME 03 (BOTTOM NAVBAR — ALL STATES) و §FRAME 14 (MOBILE INTERACTION NOTES) — پیش‌نمایش با پیکربندی واقعی تولید (shell/navConfig.ts)"
        >
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <MobilePreviewFrame label="DEFAULT · فعال = اعلان‌ها">
              <V2BottomNav
                items={defaultItems}
                renderLink={(item, content) => <span key={item.id}>{content}</span>}
              />
            </MobilePreviewFrame>
            <MobilePreviewFrame label="DISABLED · کارها + افزودن بدون دسترسی">
              <V2BottomNav
                items={disabledItems}
                renderLink={(item, content) => <span key={item.id}>{content}</span>}
              />
            </MobilePreviewFrame>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MOBILE_COMPLIANCE_ROWS.map((row) => (
              <div key={row.rule} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <V2Badge tone={row.status === 'ok' ? 'success' : 'warn'}>
                  {row.status === 'ok' ? 'منطبق' : 'اصلاح‌شده امروز'}
                </V2Badge>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{row.rule}</span>
                  <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{row.note}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          title="منابع طراحی"
          subtitle="بردهای Claude Design که کل رابط کاربری از روی آن‌ها ساخته شده"
        >
          <ul
            style={{
              margin: 0,
              paddingInlineStart: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {DESIGN_REFERENCES.map((ref) => (
              <li key={ref} style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                {ref}
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </OperatorPage>
  );
}
